import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import dayjs from 'dayjs';
import { Alert, Autocomplete, Divider, MenuItem, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import {
  StyledModal,
  StyledModalBody,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalInnerWrapper,
} from '../GraphModal/GraphModal';
import { getEdgeCategoryOptions } from '../../edgeCategories';
import { normalizeGraphType } from '../../graphTypes';

const ADD_EDGE_MUTATION = gql`
  mutation AddEdge($person1: ID!, $person2: ID!, $notes: String!, $dateMet: Date!, $category: String, $graphName: String) {
    addEdge(person1: $person1, person2: $person2, notes: $notes, dateMet: $dateMet, category: $category, graphName: $graphName) {
      _id
    }
  }
`;

const imageStyle = {
  width: 'clamp(50px, 14vw, 88px)',
  height: 'clamp(50px, 14vw, 88px)',
  margin: '0 auto',
  display: 'block',
};

const dashedLineStyle = {
  border: '1px dashed #000',
  width: '100%',
  height: '0px',
};

export function CreateExistingEdgeModal({
  people = [],
  isModalOpen,
  setIsModalOpen,
  refetchGraph,
  graphName,
  graphType = 'social',
}) {
  const [addEdge, { loading: isSaving, error: saveError }] = useMutation(ADD_EDGE_MUTATION);
  const [person1, setPerson1] = useState(null);
  const [person2, setPerson2] = useState(null);
  const [dateMet, setDateMet] = useState(null);
  const [category, setCategory] = useState('friendship');
  const [notes, setNotes] = useState('');
  const categoryOptions = useMemo(() => getEdgeCategoryOptions(normalizeGraphType(graphType)), [graphType]);
  const defaultCategory = categoryOptions[0]?.value || 'friendship';

  const secondPersonOptions = useMemo(
    () => people.filter((person) => person._id !== person1?._id),
    [people, person1]
  );

  const reset = () => {
    setPerson1(null);
    setPerson2(null);
    setDateMet(null);
    setCategory(defaultCategory);
    setNotes('');
  };

  useEffect(() => {
    setCategory((current) => categoryOptions.some((option) => option.value === current) ? current : defaultCategory);
  }, [categoryOptions, defaultCategory]);

  const close = () => {
    reset();
    setIsModalOpen(false);
  };

  const save = async () => {
    if (!person1 || !person2 || !dateMet || !notes.trim()) return;
    const parsedDate = dayjs.isDayjs(dateMet) ? dateMet.toDate() : dateMet;
    await addEdge({
      variables: {
        person1: person1._id,
        person2: person2._id,
        notes: notes.trim(),
        dateMet: parsedDate,
        category,
        graphName,
      },
    });
    await refetchGraph();
    close();
  };

  if (!isModalOpen) return null;

  return (
    <Modal open={isModalOpen} onClose={close}>
      <StyledModal className="graph-edit-modal">
        <StyledModalInnerWrapper>
          <StyledModalHeader className="graph-modal-header--compact">
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ textAlign: 'center', fontSize: { xs: 30, sm: 32 }, lineHeight: 1.05 }}
            >
              Add Story
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{ maxWidth: 320, fontSize: { xs: 16, sm: 14 }, lineHeight: 1.35 }}
            >
              Save how two existing people first met without adding another map line.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 1 },
                width: '100%',
                maxWidth: 420,
                mt: { xs: 0.5, sm: 1 },
              }}
            >
              <Box sx={{ minWidth: { xs: 76, sm: 96 }, textAlign: 'center', minInlineSize: 0 }}>
                <Avatar
                  alt={person1?.name}
                  src={person1?.imageURL || (person1 ? `https://robohash.org/${person1.name}` : '')}
                  sx={imageStyle}
                />
                <Typography variant="subtitle2" noWrap sx={{ fontSize: { xs: 15, sm: 14 } }}>{person1?.name || 'Person one'}</Typography>
              </Box>
              <Box sx={{ flex: 1, pt: { xs: 1.5, sm: 2 } }}>
                <Divider style={dashedLineStyle} />
              </Box>
              <Box sx={{ minWidth: { xs: 76, sm: 96 }, textAlign: 'center', minInlineSize: 0 }}>
                <Avatar
                  alt={person2?.name}
                  src={person2?.imageURL || (person2 ? `https://robohash.org/${person2.name}` : '')}
                  sx={imageStyle}
                />
                <Typography variant="subtitle2" noWrap sx={{ fontSize: { xs: 15, sm: 14 } }}>{person2?.name || 'Person two'}</Typography>
              </Box>
            </Box>
          </StyledModalHeader>

          <StyledModalBody>
            {saveError && <Alert severity="error">{saveError.message}</Alert>}
            <Autocomplete
              options={people}
              value={person1}
              getOptionLabel={(person) => person?.name || ''}
              onChange={(_, value) => {
                setPerson1(value);
                if (value?._id === person2?._id) setPerson2(null);
              }}
              renderInput={(params) => <TextField {...params} label="First person" />}
            />
            <Autocomplete
              options={secondPersonOptions}
              value={person2}
              getOptionLabel={(person) => person?.name || ''}
              onChange={(_, value) => setPerson2(value)}
              renderInput={(params) => <TextField {...params} label="Second person" />}
              disabled={!person1}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="When did they meet?"
                value={dateMet}
                onChange={setDateMet}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
            <TextField
              select
              label="Meeting category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              fullWidth
            >
              {categoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="text"
              multiline
              minRows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              label="How they met"
              fullWidth
            />
          </StyledModalBody>

          <StyledModalFooter>
            <Button variant="contained" onClick={save} disabled={isSaving || !person1 || !person2 || !dateMet || !notes.trim()}>
              Save Story
            </Button>
            <Button variant="outlined" onClick={close} disabled={isSaving}>
              Cancel
            </Button>
          </StyledModalFooter>
        </StyledModalInnerWrapper>
      </StyledModal>
    </Modal>
  );
}
